// <RequireModule code="..." modules={...} role={...}> wrapper.
//
// Enabled module → children render normally.
// Disabled + project admin / user → contact-provider notice (or custom fallback).
// Disabled + platform superadmin/support → module promo (animation + CTA to store).

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';
import UpgradeCta from './UpgradeCta';
import ModuleDisabledNotice from './ModuleDisabledNotice';
import { encodeReturnTo } from './store/storeUrls';

const PRIVILEGED_ROLES = new Set(['superadmin', 'support']);

function isModuleEnabled(modules, code) {
  if (!modules || typeof modules !== 'object') return true;
  return modules[code] === true;
}

export default function RequireModule({
  code,
  modules,
  role,
  moduleName,
  description,
  projectSlug,
  returnTo,
  fallback,
  ctaSize = 'normal',
  overlayMinHeight,
  children,
}) {
  const location = useLocation();
  const { projectModules: pm } = usePublicUiLocale();
  const meta = useMemo(() => {
    const fromLocale = pm.modules[code] || {};
    return {
      moduleName: moduleName || fromLocale.name || code,
      description: description ?? fromLocale.description ?? '',
    };
  }, [pm, code, moduleName, description]);

  const minHeight =
    overlayMinHeight ?? (ctaSize === 'compact' ? 200 : 360);

  const enabled = isModuleEnabled(modules, code);
  const privileged = PRIVILEGED_ROLES.has(role);
  const effectiveReturnTo = returnTo || encodeReturnTo(location.pathname + location.search);

  if (enabled) {
    return <>{children}</>;
  }

  if (!privileged) {
    if (fallback !== undefined) return fallback;
    return (
      <ModuleDisabledNotice
        moduleName={meta.moduleName}
        size={ctaSize}
        minHeight={minHeight}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight,
        p: ctaSize === 'compact' ? 1 : 2,
      }}
    >
      <UpgradeCta
        moduleCode={code}
        moduleName={meta.moduleName}
        description={meta.description}
        projectSlug={projectSlug}
        returnTo={effectiveReturnTo}
        size={ctaSize}
      />
    </Box>
  );
}

RequireModule.propTypes = {
  code: PropTypes.string.isRequired,
  modules: PropTypes.object,
  role: PropTypes.string,
  moduleName: PropTypes.string,
  description: PropTypes.string,
  projectSlug: PropTypes.string,
  returnTo: PropTypes.string,
  fallback: PropTypes.node,
  ctaSize: PropTypes.oneOf(['compact', 'normal']),
  overlayMinHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
};

export { isModuleEnabled };
